import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

export const KyuStoreCleanEsport: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Clean Commercial Esport Palette (Official Kyu Store Tokens)
  const bgCanvas = "#F8F6FE";
  const bgCard = "#FFFFFF";
  const kyuPurple = "#5D3FD3";
  const kyuDark = "#1E124A";
  const kyuLavender = "#F3E8FF";
  const kyuBorder = "#E9D5FF";
  const accentGold = "#FF9F43";
  const textDark = "#0F172A";
  const textMuted = "#64748B";
  const emeraldSuccess = "#10B981";
  const emeraldLight = "#ECFDF5";

  // Subtitle Timestamps (Visual Placeholder in Safe Zone Bottom 220px)
  const SUBTITLES = [
    { from: 0, to: 90, text: "Mau top-up diamond game tapi malas nunggu lama?", highlight: "malas nunggu lama" },
    { from: 90, to: 180, text: "Kyu Store: Proses instan 1 detik langsung masuk ke akunmu.", highlight: "instan 1 detik" },
    { from: 180, to: 270, text: "Weekly Diamond Pass hemat sampai 70%, bayar scan QRIS instan.", highlight: "hemat sampai 70%" },
    { from: 270, to: 360, text: "100% legal, aman, anti-minus. Top-up sekarang di kyustore.com.", highlight: "kyustore.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // Springs Helper
  const sp = (delay: number, damping = 15, stiffness = 120) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Ambient micro-motion (breathing float)
  const floatY = Math.sin(frame / 20) * 4;

  // ==========================================
  // CHAPTER TIMELINE (360 frames = 12s @ 30fps)
  // Phase 1 (Game & Package Showcase): Frame 0 - 180
  // Phase 2 (Instant QRIS & Satset Dispatch): Frame 180 - 360
  // ==========================================

  // --- Phase 1 Animations ---
  const headerSpring = sp(5, 14, 130);
  const headerY = interpolate(headerSpring, [0, 1], [-40, 0]);
  const headerOpacity = interpolate(headerSpring, [0, 1], [0, 1]);

  // Main Card Staging Entry
  const mainStageSpring = sp(15, 15, 110);
  const mainStageY = interpolate(mainStageSpring, [0, 1], [60, 0]);
  const mainStageScale = interpolate(mainStageSpring, [0, 1], [0.92, 1]);
  const mainStageOpacity = interpolate(mainStageSpring, [0, 1], [0, 1]);

  // Staggered Package Cards Entry
  const card1Spring = sp(35, 14, 140);
  const card2Spring = sp(50, 13, 140);
  const card3Spring = sp(65, 14, 140);
  const bannerBottomSpring = sp(80, 14, 130);

  // Cursor Click Interaction (Frame 90 - 135)
  const cursorProgress = interpolate(
    frame,
    [90, 120],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const cursorX = interpolate(cursorProgress, [0, 1], [700, 230]);
  const cursorY = interpolate(cursorProgress, [0, 1], [460, 280]);
  const isClicking = frame >= 122 && frame <= 132;
  const cursorScale = isClicking ? 0.85 : 1.0;
  const cursorOpacity = interpolate(frame, [90, 100], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // Phase 1 Exit / Phase 2 Entry (Frame 170 - 195)
  const p1Exit = interpolate(
    frame,
    [170, 190],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const p1ExitY = interpolate(p1Exit, [0, 1], [0, -60]);
  const p1ExitOpacity = interpolate(p1Exit, [0, 0.7], [1, 0]);

  // --- Phase 2 Animations (QRIS Checkout & Instant Verification) ---
  const p2Entry = interpolate(
    frame,
    [180, 205],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const p2EntryY = interpolate(p2Entry, [0, 1], [60, 0]);
  const p2EntryScale = interpolate(p2Entry, [0, 1], [0.9, 1]);
  const p2EntryOpacity = interpolate(p2Entry, [0, 0.5], [0, 1]);

  // Laser Scan Loop
  const laserBeamY = Math.sin(frame / 5) * 48;

  // Success Notification Pop (Frame 230)
  const successSpring = sp(230, 13, 160);
  const successY = interpolate(successSpring, [0, 1], [50, 0]);
  const successScale = interpolate(successSpring, [0, 1], [0.85, 1]);
  const successOpacity = interpolate(successSpring, [0, 1], [0, 1]);

  // SatSet Proses Badge Floating Bob
  const satsetFloatY = Math.sin(frame / 12) * 6;

  // CTA Pulse
  const ctaPulse = Math.sin(frame / 8) * 3;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgCanvas,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Manrope', 'SF Pro Display', sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* Background Architectural Esport Grid Lines */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, rgba(93, 63, 211, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(93, 63, 211, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          pointerEvents: "none",
        }}
      />

      {/* Subtle Warm Lavender Ambient Radial Glow */}
      <div
        style={{
          position: "absolute",
          top: "40%",
          left: "50%",
          width: 800,
          height: 800,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.09) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      {/* ========================================================================= */}
      {/* MASTER CONTENT CONTAINER: DEAD-CENTERED HORIZONTALLY & VERTICALLY         */}
      {/* (top: 48%, left: 50%, transform: translate(-50%, -50%))                   */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          top: "48%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 880,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* ==================================================== */}
        {/* CHAPTER 1: GAME & WEEKLY PASS (Frame 0 - 180)       */}
        {/* ==================================================== */}
        {frame < 195 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${mainStageY + p1ExitY + floatY}px) scale(${mainStageScale})`,
              opacity: mainStageOpacity * p1ExitOpacity,
            }}
          >
            {/* Header Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: bgCard,
                border: `2px solid ${kyuBorder}`,
                padding: "10px 24px",
                borderRadius: 30,
                boxShadow: "0 6px 20px rgba(93, 63, 211, 0.08)",
                marginBottom: 20,
                transform: `translateY(${headerY}px)`,
                opacity: headerOpacity,
              }}
            >
              <Img src={staticFile("kyu_logo.png")} style={{ height: 26, objectFit: "contain" }} />
              <div style={{ width: 1.5, height: 18, backgroundColor: "#E2E8F0" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 18, fontWeight: 900, color: kyuPurple }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                </svg>
                <span>TOP UP GAME RESMI & LEGAL</span>
              </div>
            </div>

            <h1
              style={{
                fontSize: 56,
                fontWeight: 900,
                lineHeight: 1.15,
                textAlign: "center",
                letterSpacing: "-0.03em",
                marginBottom: 26,
              }}
            >
              Mobile Legends Satset, <br />
              <span style={{ color: kyuPurple }}>Harga Hemat Anti-Boncos!</span>
            </h1>

            {/* Game Selector Tab Ribbon */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: bgCard,
                padding: "8px 12px",
                borderRadius: 22,
                border: "1.5px solid #E2E8F0",
                boxShadow: "0 8px 25px rgba(0,0,0,0.04)",
                marginBottom: 24,
              }}
            >
              {/* Active Tab: MLBB */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  backgroundColor: kyuPurple,
                  color: "#FFFFFF",
                  padding: "8px 20px",
                  borderRadius: 16,
                  fontWeight: 900,
                  fontSize: 16,
                  boxShadow: "0 4px 15px rgba(93, 63, 211, 0.3)",
                }}
              >
                <Img src={staticFile("game_mlbb.jpg")} style={{ width: 28, height: 28, borderRadius: 8, objectFit: "cover" }} />
                <span>Mobile Legends</span>
              </div>

              {/* Inactive Tab 2: Free Fire */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", color: textMuted, fontWeight: 700, fontSize: 15 }}>
                <Img src={staticFile("game_ff.png")} style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover" }} />
                <span>Free Fire</span>
              </div>

              {/* Inactive Tab 3: Valorant */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", color: textMuted, fontWeight: 700, fontSize: 15 }}>
                <Img src={staticFile("game_valorant.jpg")} style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover" }} />
                <span>Valorant</span>
              </div>
            </div>

            {/* Product Pricing Grid (3 Clean Commercial Cards with Pure Vector Diamonds) */}
            <div style={{ width: 860, display: "flex", gap: 18, position: "relative" }}>
              {/* Card 1: Weekly Diamond Pass (Featured) */}
              <div
                style={{
                  flex: 1.25,
                  backgroundColor: bgCard,
                  borderRadius: 28,
                  border: isClicking ? `2.5px solid ${accentGold}` : `2.5px solid ${kyuPurple}`,
                  padding: "24px 22px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  boxShadow: "0 20px 45px rgba(93, 63, 211, 0.16)",
                  transform: `scale(${interpolate(card1Spring, [0, 1], [0.8, isClicking ? 0.96 : 1.02])})`,
                  opacity: interpolate(card1Spring, [0, 1], [0, 1]),
                  position: "relative",
                  transition: "transform 0.08s ease, border-color 0.08s ease",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: -14,
                    backgroundColor: accentGold,
                    color: "#FFFFFF",
                    fontSize: 12,
                    fontWeight: 900,
                    padding: "4px 14px",
                    borderRadius: 12,
                    boxShadow: "0 4px 10px rgba(255, 159, 67, 0.35)",
                  }}
                >
                  ⚡ PALING LARIS
                </div>

                {/* Pure Vector Diamond SVG (No Broken Emoji Tofu) */}
                <div style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: kyuLavender, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 3h12l4 6-10 12L2 9Z" />
                    <path d="M11 3 8 9l4 12 4-12-3-6" />
                    <path d="M2 9h20" />
                  </svg>
                </div>

                <div style={{ fontSize: 20, fontWeight: 900, color: textDark, marginTop: 8 }}>Weekly Pass</div>
                <div style={{ fontSize: 13, color: textMuted, fontWeight: 700 }}>Total 220 Diamonds</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: kyuPurple, marginTop: 10 }}>
                  Rp 27.500
                </div>
                <div
                  style={{
                    marginTop: 14,
                    width: "100%",
                    backgroundColor: isClicking ? kyuDark : kyuPurple,
                    color: "#FFFFFF",
                    padding: "10px 0",
                    borderRadius: 14,
                    fontSize: 14,
                    fontWeight: 900,
                  }}
                >
                  Beli Sekarang
                </div>
              </div>

              {/* Card 2: 86 Diamonds */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: bgCard,
                  borderRadius: 26,
                  border: "1.5px solid #E2E8F0",
                  padding: "24px 18px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.04)",
                  transform: `scale(${interpolate(card2Spring, [0, 1], [0.8, 1])})`,
                  opacity: interpolate(card2Spring, [0, 1], [0, 1]),
                }}
              >
                {/* Pure Vector Diamond SVG */}
                <div style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 3h12l4 6-10 12L2 9Z" />
                    <path d="M11 3 8 9l4 12 4-12-3-6" />
                    <path d="M2 9h20" />
                  </svg>
                </div>
                <div style={{ fontSize: 18, fontWeight: 900, color: textDark, marginTop: 8 }}>86 Diamonds</div>
                <div style={{ fontSize: 12, color: textMuted, fontWeight: 600 }}>Proses Instan</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: textDark, marginTop: 12 }}>
                  Rp 20.000
                </div>
                <div style={{ marginTop: 14, width: "100%", backgroundColor: kyuLavender, color: kyuPurple, padding: "10px 0", borderRadius: 14, fontSize: 13, fontWeight: 800 }}>
                  Pilih
                </div>
              </div>

              {/* Card 3: 257 Diamonds */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: bgCard,
                  borderRadius: 26,
                  border: "1.5px solid #E2E8F0",
                  padding: "24px 18px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.04)",
                  transform: `scale(${interpolate(card3Spring, [0, 1], [0.8, 1])})`,
                  opacity: interpolate(card3Spring, [0, 1], [0, 1]),
                }}
              >
                {/* Pure Vector Diamond SVG */}
                <div style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 3h12l4 6-10 12L2 9Z" />
                    <path d="M11 3 8 9l4 12 4-12-3-6" />
                    <path d="M2 9h20" />
                  </svg>
                </div>
                <div style={{ fontSize: 18, fontWeight: 900, color: textDark, marginTop: 8 }}>257 Diamonds</div>
                <div style={{ fontSize: 12, color: textMuted, fontWeight: 600 }}>Bonus Event</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: textDark, marginTop: 12 }}>
                  Rp 59.000
                </div>
                <div style={{ marginTop: 14, width: "100%", backgroundColor: kyuLavender, color: kyuPurple, padding: "10px 0", borderRadius: 14, fontSize: 13, fontWeight: 800 }}>
                  Pilih
                </div>
              </div>

              {/* Animated Mouse Cursor Simulation */}
              <div
                style={{
                  position: "absolute",
                  left: cursorX,
                  top: cursorY,
                  zIndex: 30,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.25))",
                  opacity: cursorOpacity,
                  transform: `scale(${cursorScale})`,
                }}
              >
                <svg width="44" height="44" viewBox="0 0 24 24" fill="#1E124A" stroke="#FFFFFF" strokeWidth="2">
                  <path d="M3 3l7 18 3-7 7-3L3 3z"/>
                </svg>
                <div
                  style={{
                    backgroundColor: isClicking ? accentGold : kyuPurple,
                    color: "white",
                    padding: "6px 14px",
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 800,
                    marginLeft: -4,
                    marginTop: 20,
                  }}
                >
                  Klik Beli
                </div>
              </div>
            </div>

            {/* Bottom Guarantee Banner (Harmonizes Vertical Hierarchy) */}
            <div
              style={{
                marginTop: 24,
                width: 860,
                backgroundColor: bgCard,
                borderRadius: 20,
                border: "1.5px solid #E2E8F0",
                padding: "16px 24px",
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                boxShadow: "0 8px 25px rgba(0,0,0,0.03)",
                transform: `translateY(${interpolate(bannerBottomSpring, [0, 1], [30, 0])}px)`,
                opacity: interpolate(bannerBottomSpring, [0, 1], [0, 1]),
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="2.5">
                  <path d="m12 14 4-4"/>
                  <path d="M3.34 19a10 10 0 1 1 17.32 0"/>
                </svg>
                <span style={{ fontSize: 15, fontWeight: 800, color: textDark }}>Proses Otomatis 24 Jam</span>
              </div>
              <div style={{ width: 1.5, height: 24, backgroundColor: "#E2E8F0" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <span style={{ fontSize: 15, fontWeight: 800, color: textDark }}>Tanpa Password Akun</span>
              </div>
              <div style={{ width: 1.5, height: 24, backgroundColor: "#E2E8F0" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={accentGold} strokeWidth="2.5">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                <span style={{ fontSize: 15, fontWeight: 800, color: textDark }}>100% Legal & Aman</span>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* CHAPTER 2: INSTANT QRIS & SATSET DISPATCH (180 - 360)*/}
        {/* ==================================================== */}
        {frame >= 175 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${p2EntryY + floatY}px) scale(${p2EntryScale})`,
              opacity: p2EntryOpacity,
            }}
          >
            {/* SatSet Official Badge Floating Header */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 20,
                transform: `translateY(${satsetFloatY}px)`,
              }}
            >
              <Img src={staticFile("satset_proses.png")} style={{ height: 44, objectFit: "contain" }} />
            </div>

            <h1
              style={{
                fontSize: 54,
                fontWeight: 900,
                lineHeight: 1.15,
                textAlign: "center",
                letterSpacing: "-0.03em",
                marginBottom: 25,
              }}
            >
              Scan QRIS 1-Klik, <br />
              <span style={{ color: kyuPurple }}>Diamond Otomatis Masuk Akun!</span>
            </h1>

            {/* Split Screen Container (QRIS on Left, Verification on Right) */}
            <div style={{ width: 860, display: "flex", gap: 20, alignItems: "center" }}>
              {/* Left Box: QRIS Interactive Scanner */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: bgCard,
                  borderRadius: 28,
                  border: `2px solid ${kyuBorder}`,
                  padding: "24px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxShadow: "0 20px 45px rgba(93, 63, 211, 0.1)",
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 900, color: kyuDark, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  <span>QRIS SEMUA BANK & E-WALLET</span>
                </div>

                {/* QR Code Container with Animated Laser */}
                <div
                  style={{
                    width: 200,
                    height: 200,
                    backgroundColor: "#111827",
                    borderRadius: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                    <rect x="2" y="2" width="8" height="8" rx="1"></rect>
                    <rect x="14" y="2" width="8" height="8" rx="1"></rect>
                    <rect x="14" y="14" width="8" height="8" rx="1"></rect>
                    <rect x="2" y="14" width="8" height="8" rx="1"></rect>
                    <line x1="6" y1="6" x2="6" y2="6"></line>
                    <line x1="18" y1="6" x2="18" y2="6"></line>
                    <line x1="18" y1="18" x2="18" y2="18"></line>
                    <line x1="6" y1="18" x2="6" y2="18"></line>
                  </svg>
                  {/* Glowing Laser Scan Beam */}
                  <div
                    style={{
                      position: "absolute",
                      left: 10,
                      right: 10,
                      top: 100 + laserBeamY,
                      height: 4,
                      backgroundColor: "#00F0FF",
                      boxShadow: "0 0 12px #00F0FF, 0 0 20px #00F0FF",
                    }}
                  />
                </div>

                <div style={{ marginTop: 12, fontSize: 13, color: textMuted, fontWeight: 700 }}>
                  BCA • Mandiri • BRI • Dana • GoPay • OVO
                </div>
              </div>

              {/* Right Box: Injection Status Card */}
              <div
                style={{
                  flex: 1.1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  transform: `translateY(${successY}px) scale(${successScale})`,
                  opacity: successOpacity,
                }}
              >
                {/* Status Box */}
                <div
                  style={{
                    backgroundColor: bgCard,
                    borderRadius: 24,
                    border: `2px solid ${emeraldSuccess}`,
                    padding: "20px 24px",
                    boxShadow: "0 15px 35px rgba(16, 185, 129, 0.15)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 16, fontWeight: 900, color: emeraldSuccess, display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      TRANSAKSI SUKSES
                    </span>
                    <span style={{ fontSize: 13, color: "#64748B", fontWeight: 700 }}>0.8 Detik</span>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: textDark, marginTop: 6 }}>
                    Weekly Diamond Pass Aktif
                  </div>
                  <div style={{ fontSize: 13, color: textMuted, marginTop: 2 }}>
                    ID: 84920194 (2041) • Langsung masuk ke game
                  </div>
                </div>

                {/* Trust Guarantees */}
                <div
                  style={{
                    backgroundColor: bgCard,
                    borderRadius: 22,
                    border: "1.5px solid #E2E8F0",
                    padding: "16px 20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 10, backgroundColor: kyuLavender, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: textDark }}>Cukup User ID</div>
                      <div style={{ fontSize: 11, color: textMuted }}>Tanpa Password</div>
                    </div>
                  </div>

                  <div style={{ width: 1, height: 30, backgroundColor: "#E2E8F0" }} />

                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 10, backgroundColor: emeraldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="2.5">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: textDark }}>100% Legal</div>
                      <div style={{ fontSize: 11, color: textMuted }}>Anti-Minus</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Outro CTA Button */}
            <div
              style={{
                marginTop: 26,
                backgroundColor: kyuDark,
                color: "#FFFFFF",
                border: `2px solid ${accentGold}`,
                borderRadius: 34,
                padding: "20px 56px",
                fontSize: 26,
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                gap: 16,
                boxShadow: "0 20px 45px rgba(30, 18, 74, 0.35)",
                transform: `scale(${1 + ctaPulse * 0.01})`,
              }}
            >
              <span>Top Up Cepat di kyustore.com</span>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={accentGold} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: ANCHORED IN LOWER GROUNDED SAFE ZONE (Y = 1700px)     */}
      {/* ========================================================================= */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 220, // Strict user rule: subtitle wajib di safe zone bottom 220px
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(30, 18, 74, 0.94)",
              backdropFilter: "blur(14px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.16)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
              maxWidth: 840,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span style={{ fontSize: 27, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.35 }}>
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span style={{ color: accentGold }}>{currentSub.highlight}</span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
